import type { LayoutInput, LayoutResult } from '../types';
import { shapeBounds } from '../utils/geometry';

export type AlignType =
	| 'left'
	| 'center'
	| 'right'
	| 'top'
	| 'middle'
	| 'bottom';

export type DistributeType = 'horizontal' | 'vertical';

/**
 * Align nodes along a shared edge or center line.
 *
 * - left/right/center: align on the X axis
 * - top/bottom/middle: align on the Y axis
 *
 * Nodes keep their size; only position changes.
 */
export function align(
	input: LayoutInput,
	type: AlignType,
): LayoutResult {
	const { nodes } = input;
	if (nodes.length < 2) {
		return { nodes: nodes.map((n) => ({ id: n.id, ...shapeBounds(n.shape) })) };
	}

	// Compute bounds for each node
	const items = nodes.map((n) => {
		const b = shapeBounds(n.shape);
		return { id: n.id, x: b.x, y: b.y, width: b.width, height: b.height };
	});

	// Find the target line
	let target: number;
	switch (type) {
		case 'left':
			target = Math.min(...items.map((i) => i.x));
			for (const item of items) item.x = target;
			break;
		case 'center': {
			const minX = Math.min(...items.map((i) => i.x));
			const maxX = Math.max(...items.map((i) => i.x + i.width));
			target = (minX + maxX) / 2;
			for (const item of items) item.x = target - item.width / 2;
			break;
		}
		case 'right':
			target = Math.max(...items.map((i) => i.x + i.width));
			for (const item of items) item.x = target - item.width;
			break;
		case 'top':
			target = Math.min(...items.map((i) => i.y));
			for (const item of items) item.y = target;
			break;
		case 'middle': {
			const minY = Math.min(...items.map((i) => i.y));
			const maxY = Math.max(...items.map((i) => i.y + i.height));
			target = (minY + maxY) / 2;
			for (const item of items) item.y = target - item.height / 2;
			break;
		}
		case 'bottom':
			target = Math.max(...items.map((i) => i.y + i.height));
			for (const item of items) item.y = target - item.height;
			break;
	}

	return {
		nodes: items.map((i) => ({
			id: i.id,
			x: i.x,
			y: i.y,
			width: i.width,
			height: i.height,
		})),
	};
}

/**
 * Distribute nodes evenly along an axis.
 *
 * - horizontal: equal spacing between nodes left-to-right
 * - vertical: equal spacing between nodes top-to-bottom
 *
 * The outermost nodes stay in place; interior nodes are repositioned
 * so the gaps between them are equal.
 * Requires 3+ nodes to have any effect.
 */
export function distribute(
	input: LayoutInput,
	type: DistributeType,
): LayoutResult {
	const { nodes } = input;
	if (nodes.length < 3) {
		return { nodes: nodes.map((n) => ({ id: n.id, ...shapeBounds(n.shape) })) };
	}

	const items = nodes.map((n) => {
		const b = shapeBounds(n.shape);
		return { id: n.id, x: b.x, y: b.y, width: b.width, height: b.height };
	});

	if (type === 'horizontal') {
		// Sort by left edge
		items.sort((a, b) => a.x - b.x);

		const first = items[0];
		const last = items[items.length - 1];
		const totalSpan = last.x + last.width - first.x;
		const totalNodeWidth = items.reduce((s, i) => s + i.width, 0);
		const gap = (totalSpan - totalNodeWidth) / (items.length - 1);

		let cursor = first.x;
		for (const item of items) {
			item.x = cursor;
			cursor += item.width + gap;
		}
	} else {
		// Sort by top edge
		items.sort((a, b) => a.y - b.y);

		const first = items[0];
		const last = items[items.length - 1];
		const totalSpan = last.y + last.height - first.y;
		const totalNodeHeight = items.reduce((s, i) => s + i.height, 0);
		const gap = (totalSpan - totalNodeHeight) / (items.length - 1);

		let cursor = first.y;
		for (const item of items) {
			item.y = cursor;
			cursor += item.height + gap;
		}
	}

	return {
		nodes: items.map((i) => ({
			id: i.id,
			x: i.x,
			y: i.y,
			width: i.width,
			height: i.height,
		})),
	};
}
